import { useEffect, useMemo, useRef, useState } from 'react';

import { useSubscription } from 'react-stomp-hooks';
import { logger } from 'utilitys/logger/logger';

export const NARRATIVE_GENERATED_TYPE = 'NarrativeGenerated';

export interface NarrativeGeneratedMessage {
    key: string;
    value: string;
}

export interface ParsedMessage {
    type: string;
    message: NarrativeGeneratedMessage;
}

interface useNarrativeParams {
    email: string | undefined;
    widgetId: string | undefined;
    showGenerativeNarrative: boolean;
    description: string | undefined;
}

export const MAX_WAIT_MS = 10 * 60 * 1000; // 10 minutes
export const TIMEOUT_MESSAGE = 'No results match the applied filter.';

export function useNarrative({ email, widgetId, showGenerativeNarrative, description }: useNarrativeParams) {
    // Control if we want to show generated narrative by default
    // "showGenerativeNarrative" is a state saved per each widget in pinboard/report
    const [isGenerativeAi, setIsGenerativeAi] = useState<boolean>(showGenerativeNarrative);

    // This will store the generated text by the backend
    const [generatedNarrative, setGeneratedNarrative] = useState<string | undefined>(undefined);

    // derive loading: we want AI text AND we don't have it yet
    const isLoading = isGenerativeAi && !generatedNarrative;
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Subscribe to notifications
    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body) as ParsedMessage;
        if (type === NARRATIVE_GENERATED_TYPE) {
            logger.debug('narrative message received', { type, message });
            const { key: widgetIdApiResponse, value: generatedNarrativeApiResponse } = message;
            const matchesCurrentWidget = Number(widgetIdApiResponse) === Number(widgetId);
            // if current widgetId matches the widgetId from message then this narrative belong to this widget
            if (matchesCurrentWidget) {
                setGeneratedNarrative(generatedNarrativeApiResponse);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            }
        }
    });

    // Timeout guard (3 minutes max)
    useEffect(() => {
        // If we're NOT currently loading, and a previous timeout exists, cancel that pending timeout and clear the ref.
        if (!isLoading) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        // If we ARE loading, ensure we don't have a leftover timer from a previous cycle.
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        // Arm a new timeout for the max wait window.
        timeoutRef.current = setTimeout(() => {
            setGeneratedNarrative(TIMEOUT_MESSAGE);
            logger.warn('Generative narrative timed out');
        }, MAX_WAIT_MS);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isLoading]);

    // Keep local isGenerativeAi in sync if parent toggles the prop later
    useEffect(() => {
        setIsGenerativeAi(showGenerativeNarrative);
    }, [showGenerativeNarrative]);

    // Choose the narrative to display: either the Backend-generated one or user customized regular description
    const narrativeDescription = useMemo(() => {
        return isGenerativeAi ? generatedNarrative : (description ?? ' ');
    }, [isGenerativeAi, generatedNarrative, description]);

    return { narrativeDescription, isGenerativeAi, setIsGenerativeAi, isLoading };
}
